// File: D:\Core Technologies\amer-admin-webapp\src\v2\services\api.js
// This module wraps the fetch API and enforces the unified response structure.
// All API responses are expected to return an object with keys: status, message, data, (optional) errors, and pagination.
import { API_BASE_URL } from "configs/AppConfig";
import { handleErrors } from "services/errors";
import { LANGUAGE, normalizeValue } from "services/general";
import { getSubdomain } from "utils";
import { API_CENTRAL_URL } from "configs/AppConfig";
import { setGlobalNetworkError } from "utils/globalNetworkError";
import { ERROR_STATUS_CODES_VALUES } from "constants/status-codes";
import { trackApiRequest } from "posthog";

export const API_BASE_KEYS = { TENANT: "tenant", CENTRAL: "central" };

/**
 * Where the HTTP status is stamped on a returned error envelope.
 *
 * `customFetch` resolves on an API error rather than throwing, so React Query
 * records the query as successful and its own `error` is never set. Without
 * this key the status is gone by the time a component sees the response, and a
 * missing record (404) is indistinguishable from an endpoint that answered
 * nothing. Read it through `useApiQuery`'s `notFound` / `httpStatus`.
 */
export const HTTP_STATUS_KEY = "__httpStatus";

const getBaseUrl = (baseKey) => {
  if (baseKey === API_BASE_KEYS.CENTRAL) {
    return API_CENTRAL_URL;
  }
  return API_BASE_URL.replace("TENANT_SLUG", getSubdomain());
};

const customFetch = async (url, options = {}) => {
  const isCentral = options.baseUrl === API_BASE_KEYS.CENTRAL;
  const token = isCentral
    ? localStorage.getItem("central_access_token")
    : localStorage.getItem("token");

  // Setup authorization headers.
  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  };

  let requestBody;

  if (options.body) {
    if (options.body instanceof FormData) {
      // Handle FormData case without overwriting files
      requestBody = new FormData();
      options.body.forEach((value, key) => {
        const v = normalizeValue(value);
        requestBody.append(key, v);
      });
    } else {
      // Prepare a JSON request body filtering out undefined values
      requestBody = Object.entries(options.body).reduce((acc, [key, value]) => {
        const v = normalizeValue(value);
        acc[key] = v;
        return acc;
      }, {});
      requestBody = JSON.stringify(requestBody);
      headers["Content-Type"] = "application/json";
    }
  }

  try {
    const baseUrl = getBaseUrl(options.baseUrl);
    const response = await fetch(`${baseUrl}/${LANGUAGE}/${url}`, {
      method: options.method || "GET",
      headers: headers,
      body: requestBody,
      signal: options.signal,
    });

    const contentType = response.headers.get("content-type") || "";
    let formatedResponse;
    // Handle different response types (JSON, blob, text)
    if (options.responseType === "blob") {
      return await response.blob();
    } else if (contentType.includes("application/json")) {
      formatedResponse = await response.json();
      const isError =
        formatedResponse?.status === false ||
        formatedResponse?.success === false ||
        !response?.ok ||
        ERROR_STATUS_CODES_VALUES.includes(response?.status);
      // Product analytics for every request, in one place — see API_EVENTS.
      if (!isError && formatedResponse?.status) {
        trackApiRequest({
          url,
          method: options.method,
          body: options.body,
          response: formatedResponse,
          meta: options.analytics,
        });
      }
      if (options?.returnFullErrorResponse) {
        return formatedResponse;
      }
      if (isError) {
        handleErrors(formatedResponse, response?.status, {
          suppressNotFoundToast: options.suppressNotFoundToast,
        });
        // Only a plain object can carry the status — an error payload is
        // sometimes an array, and spreading one into an object would break the
        // `Array.isArray` branches in `useApiMutation`.
        if (
          formatedResponse &&
          typeof formatedResponse === "object" &&
          !Array.isArray(formatedResponse)
        ) {
          return { ...formatedResponse, [HTTP_STATUS_KEY]: response?.status };
        }
        return formatedResponse;
      }
      return formatedResponse;
    } else if (contentType.includes("text/html")) {
      return await response.text();
    } else {
      return await response.blob();
    }
  } catch (err) {
    if (options.suppressGlobalError) {
      throw err;
    }
    setGlobalNetworkError(err);
  }
};

export default customFetch;
